import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import styles from './BackofficeLayout.module.scss';
import styled from 'styled-components';
import BotoneraModern from '../Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import BackofficeHeader from './components/Shared/BackofficeHeader.tsx';
import TabNavigation from './components/Shared/TabNavigation.tsx';
import IdentityValidationSection from './components/IdentityValidation/IdentityValidationSection.tsx';
import AnteProyectosValidationSection from './components/AnteProyectosValidation/AnteProyectosValidationSection.tsx';
import AdminDashboardSection from './components/AdminDashboard/AdminDashboardSection.tsx';
import AdminAnalyticsSection from './components/AdminAnalytics/AdminAnalyticsSection.tsx';
import CuratedPostulationsSection from './components/CuratedPostulations/CuratedPostulationsSection.tsx';
import { fetchIdentityPhotos, validatedIdentityWorker } from '../../store/actions/registerActions';
import {
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto,
  fetchCuratedJobRequests,
  fetchCuratedPostulations,
} from '../../store/actions/backofficeActions.ts';
import { fetchAdminDashboardStats } from '../../store/actions/dashboardActions';
import { BackofficeFilters, BackofficeTab } from '../../types/backoffice.ts';
import {
  fetchAdminAnalyticsOverview,
  setAdminAnalyticsFilters,
} from '../../store/actions/adminAnalyticsActions.ts';
import { AdminAnalyticsFilters } from '../../types/adminAnalytics.ts';

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding-right: 2%;
  padding-left: 2%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 1200px) {
    padding-right: 2%;
    padding-left: 2%;
  }

  @media (max-width: 768px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

interface BackofficeLayoutProps {
  fetchIdentityPhotos: () => void;
  workBagUsersToValidate: any[];
  validatedIdentityWorker: (userId: string, isValid: boolean) => void;
  activeTab: BackofficeTab;
  analyticsOverview: any;
  analyticsLoading: boolean;
  analyticsError: string | null;
  analyticsFilters: AdminAnalyticsFilters;
  pendingAnteProyectos: any[];
  stats: any;
  statsLoading: boolean;
  anteProyectosLoading: boolean;
  pagination: any;
  filters: BackofficeFilters;
  validationLoading: boolean;
  dashboardStats: any;
  dashboardStatsLoading: boolean;
  curatedJobRequests: any[];
  curatedJobRequestsLoading: boolean;
  curatedJobRequestsError: string | null;
  curatedSelectedJobRequest: any | null;
  curatedPostulations: any[];
  curatedPostulationsLoading: boolean;
  curatedPostulationsError: string | null;
  setActiveTab: (tab: BackofficeTab) => void;
  fetchAdminAnalyticsOverview: () => void;
  setAdminAnalyticsFilters: (filters: Partial<AdminAnalyticsFilters>) => void;
  fetchPendingAnteProyectos: (page: number, filters?: Partial<BackofficeFilters>) => void;
  fetchBackofficeStats: () => void;
  setBackofficeFilters: (filters: Partial<BackofficeFilters>) => void;
  setBackofficePage: (page: number) => void;
  validateAnteProyecto: (validation: any) => Promise<any>;
  fetchAdminDashboardStats: () => void;
  fetchCuratedJobRequests: () => void;
  fetchCuratedPostulations: (jobRequestId: number) => void;
}

const getAdminDashboardStats = (dashboard: any) => {
  const totalUsers = Array.isArray(dashboard?.totalUsers) ? dashboard.totalUsers.length : (dashboard?.totalUsers ?? 0);
  const totalPublicProjects = typeof dashboard?.totalPublicProjects === 'number' ? dashboard.totalPublicProjects : (Array.isArray(dashboard?.totalPublicProjects) ? dashboard.totalPublicProjects.length : 0);
  const totalPrivateProjects = Array.isArray(dashboard?.totalPrivateProjects) ? dashboard.totalPrivateProjects.length : (dashboard?.totalPrivateProjects ?? 0);
  const totalPublicQuotations = typeof dashboard?.totalPublicQuotations === 'number' ? dashboard.totalPublicQuotations : (Array.isArray(dashboard?.totalPublicQuotations) ? dashboard.totalPublicQuotations.length : 0);
  const totalPrivateQuotations = Array.isArray(dashboard?.totalPrivateQuotations) ? dashboard.totalPrivateQuotations.length : (dashboard?.totalPrivateQuotations ?? 0);
  return {
    totalUsers,
    totalPublicProjects,
    totalPrivateProjects,
    totalPublicQuotations,
    totalPrivateQuotations
  };
};

const getIdentityStatusFromRow = (row: any): 'pending' | 'approved' | 'rejected' => {
  const nestedUser = row?.user ?? row?.jobBoard ?? row;
  const legacy = nestedUser?.identity_validated ?? row?.identity_validated;
  if (legacy === true) return 'approved';
  if (legacy === false) return 'rejected';
  return 'pending';
};

const BackofficeLayout: React.FC<BackofficeLayoutProps> = ({
  fetchIdentityPhotos,
  workBagUsersToValidate,
  validatedIdentityWorker,
  activeTab,
  pendingAnteProyectos,
  stats,
  statsLoading,
  anteProyectosLoading,
  pagination,
  filters,
  validationLoading,
  dashboardStats,
  dashboardStatsLoading,
  curatedJobRequests,
  curatedJobRequestsLoading,
  curatedJobRequestsError,
  curatedSelectedJobRequest,
  curatedPostulations,
  curatedPostulationsLoading,
  curatedPostulationsError,
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto,
  fetchAdminDashboardStats,
  fetchCuratedJobRequests,
  fetchCuratedPostulations,
  analyticsOverview,
  analyticsLoading,
  analyticsError,
  analyticsFilters,
  fetchAdminAnalyticsOverview,
  setAdminAnalyticsFilters,
}) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const identityPendingCount = (workBagUsersToValidate || []).filter(
    (row) => getIdentityStatusFromRow(row) === 'pending'
  ).length;

  useEffect(() => {
    fetchIdentityPhotos();
    fetchBackofficeStats();
  }, [fetchIdentityPhotos, fetchBackofficeStats]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAdminDashboardStats();
    }
  }, [activeTab, fetchAdminDashboardStats]);

  useEffect(() => {
    if (activeTab === 'anteproyectos') {
      fetchPendingAnteProyectos(pagination.currentPage, filters);
    }
  }, [activeTab, fetchPendingAnteProyectos, pagination.currentPage, filters]);

  useEffect(() => {
    if (activeTab === 'curated-postulations') {
      fetchCuratedJobRequests();
    }
  }, [activeTab, fetchCuratedJobRequests]);

  useEffect(() => {
    if (activeTab === 'estadisticas') {
      fetchAdminAnalyticsOverview();
    }
  }, [activeTab, fetchAdminAnalyticsOverview]);

  const handleRefresh = useCallback(() => {
    if (activeTab === 'identity') {
      fetchIdentityPhotos();
    } else if (activeTab === 'anteproyectos') {
      fetchPendingAnteProyectos(pagination.currentPage, filters);
    } else if (activeTab === 'dashboard') {
      fetchAdminDashboardStats();
    } else if (activeTab === 'curated-postulations') {
      fetchCuratedJobRequests();
    } else if (activeTab === 'estadisticas') {
      fetchAdminAnalyticsOverview();
    }
    fetchBackofficeStats();
  }, [activeTab, fetchIdentityPhotos, fetchPendingAnteProyectos, pagination.currentPage, filters, fetchBackofficeStats, fetchAdminDashboardStats, fetchCuratedJobRequests, fetchAdminAnalyticsOverview]);

  const handleIdentityValidation = (userId: string, isValid: boolean) => {
    validatedIdentityWorker(userId, isValid);
    setTimeout(() => {
      fetchIdentityPhotos();
      fetchBackofficeStats();
    }, 500);
  };

  const handleAnteProyectoValidation = async (
    id: string,
    isApproved: boolean,
    reason?: string,
    adminNote?: string
  ) => {
    const result = await validateAnteProyecto({ id, isApproved, reason, adminNote });
    if (result.success) {
      setTimeout(() => {
        fetchPendingAnteProyectos(pagination.currentPage, filters);
        fetchBackofficeStats();
      }, 500);
    }
  };

  const handleTabChange = (tab: BackofficeTab) => {
    setActiveTab(tab);
  };

  return (
    <Container>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      
      <div className={styles.backofficeLayout}>
        <BackofficeHeader 
          stats={stats} 
          loading={statsLoading}
          onRefresh={handleRefresh}
          identityPendingCount={identityPendingCount}
        />
        
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          identityCount={identityPendingCount}
          projectsCount={stats.totalPendingAnteProyectos}
          curatedCount={
            (curatedJobRequests || []).filter((jobRequest) => (jobRequest.postulationCount ?? 0) > 0).length
          }
        />

        <div className={styles.content}>
          {activeTab === 'dashboard' && (
            <AdminDashboardSection
              stats={getAdminDashboardStats(dashboardStats)}
              loading={dashboardStatsLoading}
            />
          )}

          {activeTab === 'identity' && (
            <IdentityValidationSection
              users={workBagUsersToValidate || []}
              loading={false}
              onValidate={handleIdentityValidation}
            />
          )}

          {activeTab === 'anteproyectos' && (
            <AnteProyectosValidationSection
              anteProyectos={pendingAnteProyectos}
              loading={anteProyectosLoading}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              filters={filters}
              onPageChange={setBackofficePage}
              onFilterChange={setBackofficeFilters}
              onValidate={handleAnteProyectoValidation}
              validationLoading={validationLoading}
            />
          )}

          {activeTab === 'estadisticas' && (
            <AdminAnalyticsSection
              overview={analyticsOverview}
              filters={analyticsFilters}
              loading={analyticsLoading}
              error={analyticsError}
              onFiltersChange={setAdminAnalyticsFilters}
              onFetch={fetchAdminAnalyticsOverview}
            />
          )}

          {activeTab === 'curated-postulations' && (
            <CuratedPostulationsSection
              jobRequests={curatedJobRequests}
              jobRequestsLoading={curatedJobRequestsLoading}
              jobRequestsError={curatedJobRequestsError}
              selectedJobRequest={curatedSelectedJobRequest}
              postulations={curatedPostulations}
              postulationsLoading={curatedPostulationsLoading}
              postulationsError={curatedPostulationsError}
              onSelectJobRequest={fetchCuratedPostulations}
            />
          )}
        </div>
      </div>
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  workBagUsersToValidate: state.register.workBagUsersToValidate,
  activeTab: state.backoffice.activeTab,
  pendingAnteProyectos: state.backoffice.pendingAnteProyectos,
  stats: state.backoffice.stats,
  statsLoading: state.backoffice.statsLoading,
  anteProyectosLoading: state.backoffice.anteProyectosLoading,
  pagination: state.backoffice.pagination,
  filters: state.backoffice.filters,
  validationLoading: state.backoffice.validationLoading,
  curatedJobRequests: state.backoffice.curatedJobRequests,
  curatedJobRequestsLoading: state.backoffice.curatedJobRequestsLoading,
  curatedJobRequestsError: state.backoffice.curatedJobRequestsError,
  curatedSelectedJobRequest: state.backoffice.curatedSelectedJobRequest,
  curatedPostulations: state.backoffice.curatedPostulations,
  curatedPostulationsLoading: state.backoffice.curatedPostulationsLoading,
  curatedPostulationsError: state.backoffice.curatedPostulationsError,
  analyticsOverview: state.backoffice.analyticsOverview,
  analyticsLoading: state.backoffice.analyticsLoading,
  analyticsError: state.backoffice.analyticsError,
  analyticsFilters: state.backoffice.analyticsFilters,
  dashboardStats: state.dashboard,
  dashboardStatsLoading: false
});

export default connect(mapStateToProps, {
  fetchIdentityPhotos,
  validatedIdentityWorker,
  setActiveTab,
  fetchPendingAnteProyectos,
  fetchBackofficeStats,
  setBackofficeFilters,
  setBackofficePage,
  validateAnteProyecto,
  fetchAdminDashboardStats,
  fetchCuratedJobRequests,
  fetchCuratedPostulations,
  fetchAdminAnalyticsOverview,
  setAdminAnalyticsFilters,
})(BackofficeLayout);

